import { useEffect, useRef, useState } from 'react';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, ActionIcon } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { WhatsAppText } from '@repletoryreach/sdk-utils/whatsapp-text';
import { useT } from '../../../../i18n/I18nProvider';
import type { FlowDefinition } from '../mapper';

type Turn = flows.FlowTestTurn;
type Reply = flows.FlowTestMessage;

interface Bubble {
  who: 'bot' | 'you';
  reply?: Reply;
  text?: string;
}

/**
 * The test chat: a flow tried as the customer would see it, without a phone.
 *
 * Every message the flow sends comes back from the test endpoints and is
 * drawn as a bubble; buttons and list rows are clickable, and typing answers
 * a question. The draft on the canvas is what runs, so a change can be tried
 * before it is saved. Nothing leaves the platform and nothing is billed.
 */
export function TestPanel({
  opened, onClose, flowId, flowName, definition
}: {
  opened: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  definition: FlowDefinition | null;
}) {
  const t = useT();
  const [session, setSession] = useState<Turn | null>(null);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showState, setShowState] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const ended = session?.state === 'done';
  const waiting = session?.state === 'waiting_input';

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [bubbles.length, busy]);

  // A fresh session every time the panel opens: the draft may have changed.
  useEffect(() => {
    if (!opened) return;
    setSession(null);
    setBubbles([]);
    setDraft('');
    setError(null);
  }, [opened]);

  function absorb(turn: Turn) {
    setSession(turn);
    setBubbles((prev) => [...prev, ...turn.replies.map((r) => ({ who: 'bot' as const, reply: r }))]);
    if (turn.error) setError(turn.error);
  }

  async function start(message?: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await flows.startTest(flowId, {
        message: message || undefined,
        definition: definition ?? undefined
      });
      setBubbles(message ? [{ who: 'you', text: message }] : []);
      absorb(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.unknownError'));
    } finally {
      setBusy(false);
    }
  }

  async function send(message?: string, replyId?: string, label?: string) {
    if (!session) { await start(message); return; }
    if (ended) return;
    setBusy(true);
    setError(null);
    setBubbles((prev) => [...prev, { who: 'you', text: label ?? message ?? '' }]);
    try {
      const res = await flows.testTurn(flowId, session.session_id, { message, reply_id: replyId });
      absorb(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.unknownError'));
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    void send(text);
  }

  const lastBot = [...bubbles].reverse().find((b) => b.who === 'bot')?.reply;
  const options = waiting && lastBot?.kind === 'interactive' ? lastBot.options : [];

  return (
    <Drawer opened={opened} onClose={onClose} title={t('flows.test.title', { name: flowName })} position="right" size="md">
      <div className="rr-testchat">
        <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.test.hint')}</Text>

        <div className="rr-testchat__thread" ref={scrollRef} aria-live="polite">
          {bubbles.length === 0 && !busy && (
            <div className="rr-testchat__empty">
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.test.empty')}</Text>
              <Button size="sm" onClick={() => void start()}>{t('flows.test.start')}</Button>
            </div>
          )}
          {bubbles.map((b, i) => (
            <div key={i} className={`rr-testchat__bubble rr-testchat__bubble--${b.who}`}>
              {b.who === 'you' ? (
                <span>{b.text}</span>
              ) : b.reply ? (
                <BotBubble reply={b.reply} />
              ) : null}
            </div>
          ))}
          {busy && <div className="rr-testchat__bubble rr-testchat__bubble--bot rr-testchat__typing" aria-label={t('flows.test.thinking')}><span /><span /><span /></div>}
          {ended && (
            <div className="rr-testchat__ended">
              <Text role="caption" c="var(--rr-color-text-muted)">
                {session?.status === 'failed'
                  ? t('flows.test.failed')
                  : session?.status === 'handed_off' ? t('flows.test.handedOff') : t('flows.test.ended')}
              </Text>
              <Button size="xs" variant="subtle" onClick={() => { setBubbles([]); setSession(null); void start(); }}>
                {t('flows.test.restart')}
              </Button>
            </div>
          )}
        </div>

        {error && <Alert color="error" role="alert">{error}</Alert>}

        {options.length > 0 && (
          <div className="rr-testchat__options">
            {options.map((o) => (
              <Button key={o.id} size="xs" variant="outline" disabled={busy} onClick={() => void send(undefined, o.id, o.title)}>
                {o.title}
              </Button>
            ))}
          </div>
        )}

        <form className="rr-testchat__composer" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <TextInput
            aria-label={t('flows.test.placeholder')}
            placeholder={session ? (waiting ? t('flows.test.placeholderAnswer') : t('flows.test.placeholder')) : t('flows.test.placeholderFirst')}
            value={draft}
            disabled={busy || ended}
            onChange={(e) => setDraft(e.currentTarget.value)}
          />
          <Button size="sm" type="submit" disabled={busy || ended || !draft.trim()}>{t('flows.test.send')}</Button>
        </form>

        <Group justify="space-between" align="center">
          <Text role="caption" c="var(--rr-color-text-muted)">
            {session ? t('flows.test.state', { state: t(`flows.test.states.${session.state}`, session.state), node: session.current_node_id ?? '–' }) : ''}
          </Text>
          <Group gap={4}>
            <Button size="xs" variant="subtle" onClick={() => setShowState((v) => !v)} disabled={!session}>
              {showState ? t('flows.test.hideState') : t('flows.test.showState')}
            </Button>
            <ActionIcon variant="subtle" size="sm" aria-label={t('flows.test.restart')} disabled={!session || busy} onClick={() => { setBubbles([]); setSession(null); void start(); }}>
              <RestartIcon />
            </ActionIcon>
          </Group>
        </Group>

        {showState && session && (
          <Stack gap={6} className="rr-testchat__state">
            <Text role="inputLabel">{t('flows.test.variables')}</Text>
            <pre className="rr-testchat__pre">{JSON.stringify(session.variables, null, 2)}</pre>
            <Text role="inputLabel">{t('flows.test.steps')}</Text>
            <ol className="rr-testchat__steps">
              {session.steps.map((s, i) => (
                <li key={i}>
                  <code>{s.node}</code> <span className="rr-testchat__outcome" data-outcome={s.outcome}>{s.outcome}</span>
                  {s.detail ? <span className="rr-testchat__detail"> {s.detail}</span> : null}
                </li>
              ))}
            </ol>
          </Stack>
        )}
      </div>
    </Drawer>
  );
}

/**
 * A bot message as the customer would see it: text, media, or text with
 * choices.
 *
 * "As the customer would see it" is the whole contract, so the body goes
 * through the same WhatsApp renderer the inbox uses. `ChatText.ForChannel` in
 * `services/flows-llm` has already turned whatever the model wrote into
 * WhatsApp's own markup by the time a reply reaches here, and WhatsApp is not
 * Markdown: rendering it as Markdown put `_` inside a link into italics and
 * needed `dangerouslySetInnerHTML` to do it.
 */
function BotBubble({ reply }: { reply: Reply }) {
  const t = useT();
  return (
    <div className="rr-testchat__bot">
      {reply.kind === 'template' ? <div className="rr-testchat__tag">{t('flows.test.templateTag')}</div> : null}
      {reply.header ? <div className="rr-testchat__header">{reply.header}</div> : null}
      {reply.kind === 'media' && reply.media_url ? (
        reply.media_type === 'image'
          ? <img className="rr-testchat__media" src={reply.media_url} alt={reply.caption ?? ''} />
          : <a className="rr-testchat__file" href={reply.media_url} target="_blank" rel="noreferrer">{reply.media_type}</a>
      ) : null}
      {reply.text ? <div className="rr-testchat__text"><WhatsAppText value={reply.text} /></div> : null}
      {reply.caption && reply.kind === 'media' ? <div className="rr-testchat__text"><WhatsAppText value={reply.caption} /></div> : null}
      {reply.kind === 'interactive' && reply.options.length > 0 && (
        <div className="rr-testchat__choices" aria-hidden>
          {reply.options.map((o) => <span key={o.id} className="rr-testchat__choice">{o.title}</span>)}
        </div>
      )}
      {reply.footer ? <div className="rr-testchat__footer">{reply.footer}</div> : null}
    </div>
  );
}

function RestartIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.8 8a5.2 5.2 0 1 0 1.5-3.7" />
      <path d="M2.6 2.6v2.4h2.4" />
    </svg>
  );
}
